import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { APIList, OrderByDirection } from '../../../../../typings/API.interface';
import { Event } from '../../../../../typings/Event.interface';
import { GlobalContext } from '../contexts/GlobalContext';
export interface RefreshingListResult<T> {
  models: T[];
  fetched: boolean;
  count: number;
  outdated_ids: string[];
  has_next_results: boolean;
  refresh: (entry?: {
    id: string;
    [key: string]: any;
  }) => void;
  next: string | null;
  prev: string | null;
  is_loading: boolean;
  is_progressive_loading: boolean;
  scanned_until: string | null;
}
export type RefreshListOptions = {
  revalidation_interval?: number;
  limit?: number;
  include_data?: boolean;
  fetch?: boolean;
  /** If set, "what's new" polling uses this field instead of `order_by`; bookmark and probe both use it. */
  poll_column?: string;
  progressive?: boolean;
  date_filter_relative?: string;
  type?: 'events' | 'requests';
};

/** Max string value of `key` across `models` — used as the polling bookmark (server row field, not client clock). */
const maxOrderColumnValue = <T,>(models: T[], key: string): string => models.reduce((max, model) => {
  const v = String((model as Record<string, unknown>)[key] ?? '');
  return v > max ? v : max;
}, '');
const hasUnseenModels = <T extends {
  id: string;
},>(models: T[], current_model_ids: ReadonlySet<string>): boolean => models.some(model => !current_model_ids.has(model.id));

/** Merge revalidated / outdated-poll / locally-refreshed entries over the current page.
 * The ref-backed update cache is intentional previous-render state (written via the
 * returned cacheRefreshedEntry on `refresh`, read while consolidating); 'use no memo'
 * quarantines that rules-of-react violation here so React Compiler still compiles
 * useRefreshingList itself. */
const useConsolidatedModels = (current_models: APIList<any> | undefined, revalidated_models: APIList<Event> | undefined, last_outdated_response: APIList<Event> | undefined): {
  consolidated_models: any[];
  cacheRefreshedEntry: (entry: {
    id: string;
    [key: string]: any;
  }) => void;
} => {
  'use no memo';

  const update_cache = useRef<Record<string, any>>({});
  const [cacheRefreshedEntry] = useState(() => (entry: {
    id: string;
    [key: string]: any;
  }) => {
    update_cache.current[entry.id] = entry;
  });
  const consolidated_models = current_models ? current_models.models.reduce((list, model) => {
    const revalidated_event = revalidated_models?.models.find(({
      id
    }) => id === model.id);
    if (revalidated_event) {
      list.push(revalidated_event);
      return list;
    }
    const oudated_event = last_outdated_response?.models.find(({
      id: id_0
    }) => id_0 === model.id);
    if (oudated_event) {
      list.push(oudated_event);
      return list;
    }
    list.push(model);
    return list;
  }, []).map(model_0 => {
    if (update_cache.current[model_0.id]) {
      if (update_cache.current[model_0.id].updated_at >= model_0.updated_at) {
        return update_cache.current[model_0.id];
      }
      delete update_cache.current[model_0.id];
    }
    return model_0;
  }) : [];
  return {
    consolidated_models,
    cacheRefreshedEntry
  };
};

/** Omit a dynamically-named key. Module scope: computed-key destructuring in the hook body
 * makes React Compiler bail on the whole hook. */
const omitKey = (obj: Record<string, unknown>, key: string): Record<string, unknown> => {
  const {
    [key]: _omitted,
    ...rest
  } = obj;
  return rest;
};

/** Progressive continuation loop: after the initial progressive chunk, keeps fetching and
 * accumulating chunks into the SWR cache until the limit, end of results, or abort. Module
 * scope: its try/catch/finally makes React Compiler bail on the whole hook. */
const runProgressiveContinuation = async <T,>({
  data,
  abort_ctrl,
  base_filters,
  limit,
  effective_poll_column,
  list,
  mutate,
  progressive_iterations_ref,
  setPollAfterTimestamp,
  setIsProgressiveLoading,
  trackListLoaded
}: {
  data: APIList<T>;
  abort_ctrl: AbortController;
  base_filters: Record<string, unknown>;
  limit: number;
  effective_poll_column: string;
  list: (query: object, signal?: AbortSignal) => Promise<APIList<T>>;
  mutate: (value: APIList<T>, revalidate: boolean) => unknown;
  progressive_iterations_ref: {
    current: number;
  };
  setPollAfterTimestamp: (timestamp: string) => void;
  setIsProgressiveLoading: (loading: boolean) => void;
  trackListLoaded: (iterations: number) => void;
}): Promise<void> => {
  try {
    let accumulated_models: T[] = [...data.models];
    let next_cursor: string | undefined = data.pagination.next;
    // Preserve initial prev cursor for page navigation (chunks overwrite it)
    const initial_prev = data.pagination.prev;
    let last_pagination = data.pagination;
    while (!abort_ctrl.signal.aborted && next_cursor && accumulated_models.length < limit) {
      // Use the next cursor from the previous response to fetch the next chunk
      const continuation_filters: Record<string, any> = {
        ...base_filters,
        progressive: true,
        next: next_cursor
      };
      let chunk: APIList<T>;
      try {
        chunk = await list(continuation_filters, abort_ctrl.signal);
      } catch (err) {
        // Aborted or network error - stop
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          console.error('[progressive] continuation fetch failed:', err);
        }
        break;
      }
      if (abort_ctrl.signal.aborted) break;

      // A malformed chunk (e.g. null response body) ends the session early
      // with the models accumulated so far instead of crashing it
      if (!chunk?.models || !chunk.pagination) {
        console.error('[progressive] malformed continuation response, stopping');
        break;
      }
      progressive_iterations_ref.current += 1;
      accumulated_models = [...accumulated_models, ...chunk.models];
      next_cursor = chunk.pagination.next;
      last_pagination = {
        ...chunk.pagination,
        prev: initial_prev
      };

      // Update SWR cache so table shows accumulated results
      mutate({
        models: accumulated_models,
        count: accumulated_models.length,
        pagination: last_pagination
      }, false);
    }
    if (!abort_ctrl.signal.aborted) {
      const max_ts = maxOrderColumnValue(accumulated_models, effective_poll_column);
      if (max_ts) {
        setPollAfterTimestamp(max_ts);
      }
      trackListLoaded(progressive_iterations_ref.current);
    }
  } catch (err) {
    console.error('[progressive] progressive load failed:', err);
  } finally {
    // When aborted, the aborter (new SWR fetch, refresh, unmount) owns the flag;
    // otherwise always clear it so a crash can't leave the list stuck loading
    // with polling and pagination disabled
    if (!abort_ctrl.signal.aborted) {
      setIsProgressiveLoading(false);
    }
  }
};
const useRefreshingList = (getCacheKey, list, api_filters, order_by, dir, t0) => {
  const $ = _c(38);
  const {
    revalidation_interval: t1,
    limit: t2,
    include_data: t3,
    fetch: t4,
    poll_column,
    progressive: t5,
    date_filter_relative,
    type
  } = t0 === undefined ? {} as RefreshListOptions : t0;
  const revalidation_interval = t1 === undefined ? 5000 : t1;
  const limit = t2 === undefined ? 100 : t2;
  const include_data = t3 === undefined ? false : t3;
  const fetch = t4 === undefined ? true : t4;
  const progressive = t5 === undefined ? false : t5;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const effective_poll_column = poll_column ?? order_by;
  const [start_revalidation, setStartRevalidation] = useState(false);
  const [automatically_load_new_models, setLoadNewEvents] = useState(false);
  const [refresh_new_models, setRefreshNewEvents] = useState(true);
  const [poll_after_timestamp, setPollAfterTimestamp] = useState(null);
  const [is_progressive_loading, setIsProgressiveLoading] = useState(false);
  const [refreshing_key, setRefreshingKey] = useState(null);
  const progressive_abort_ref = useRef(null);
  const load_start_time_ref = useRef(null);
  const progressive_iterations_ref = useRef(0);
  const loaded_swr_key_ref = useRef(null);
  const cache_query_filters = {
    ...api_filters,
    order_by,
    dir,
    limit,
    ...(include_data ? {
      include: "data"
    } : {})
  };
  const swr_cache_key = fetch ? getCacheKey(cache_query_filters) as string : null;
  const is_full_refreshing = refreshing_key !== null && refreshing_key === swr_cache_key;
  const {
    next: _next,
    prev: _prev,
    ...base_filters
  } = cache_query_filters as Record<string, unknown>;
  const [abort_controller, setAbortController] = useState(null);
  const [last_outdated_response, setLastOutadedResponse] = useState();
  const filters_record = api_filters as Record<string, any>;
  const trackListLoaded = iterations => {
    const loading_time = load_start_time_ref.current ? Date.now() - load_start_time_ref.current : null;
    load_start_time_ref.current = null;
    const date_filter = filters_record[order_by];
    const track_event = {
      query: cache_query_filters,
      iterations,
      progressive: progressive ?? false,
      loading_time_ms: loading_time,
      date_filter_min: date_filter?.gte ?? null,
      date_filter_max: date_filter?.lte ?? null,
      date_filter_relative: date_filter_relative ?? null,
      has_search_term: !!filters_record.search_term,
      type
    };
    HookdeckAPI.track.event("List Loaded", track_event);
  };
  const revalidation_option = {
    refreshInterval: revalidation_interval,
    revalidateOnFocus: true,
    revalidateOnReconnect: true
  };
  const {
    data: current_models,
    mutate: t6
  } = useSWR(fetch && getCacheKey(cache_query_filters), () => {
    if (abort_controller) {
      abort_controller.abort();
    }
    if (progressive_abort_ref.current) {
      progressive_abort_ref.current.abort();
      progressive_abort_ref.current = null;
    }
    setIsProgressiveLoading(false);
    load_start_time_ref.current = Date.now();
    progressive_iterations_ref.current = 0;
    const controller = new AbortController();
    setAbortController(controller);
    const has_initial_data_for_active_key = loaded_swr_key_ref.current === swr_cache_key;
    const use_progressive = progressive && (!has_initial_data_for_active_key || refreshing_key !== null);
    const fetch_filters = use_progressive ? {
      ...cache_query_filters,
      progressive: true
    } : cache_query_filters;
    return list(fetch_filters, controller.signal) as any;
  }, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: data => data && data.count < limit && !is_progressive_loading ? revalidation_interval : 0,
    onSuccess: data_0 => {
      const has_initial_data_for_active_key_0 = loaded_swr_key_ref.current === swr_cache_key;
      if (refreshing_key) {
        setRefreshingKey(null);
      }
      if (!data_0.pagination.prev) {
        const max_ts = maxOrderColumnValue(data_0.models, effective_poll_column);
        setPollAfterTimestamp(max_ts || "1970-01-01T00:00:00.000Z");
      }
      if (data_0.count < limit) {
        setTimeout(() => setLoadNewEvents(true), revalidation_interval);
      } else {
        if (automatically_load_new_models) {
          setLoadNewEvents(false);
        }
      }
      if (data_0.count > 0) {
        setTimeout(() => setStartRevalidation(true), revalidation_interval);
      }
      const should_progressive_continue = progressive && (!has_initial_data_for_active_key_0 || is_full_refreshing) && data_0.count < limit && data_0.pagination.next;
      loaded_swr_key_ref.current = swr_cache_key;
      if (should_progressive_continue) {
        if (progressive_abort_ref.current) {
          progressive_abort_ref.current.abort();
        }
        const abort_ctrl = new AbortController();
        progressive_abort_ref.current = abort_ctrl;
        setIsProgressiveLoading(true);
        progressive_iterations_ref.current = 1;
        runProgressiveContinuation({
          data: data_0,
          abort_ctrl,
          base_filters,
          limit,
          effective_poll_column,
          list,
          mutate,
          progressive_iterations_ref,
          setPollAfterTimestamp,
          setIsProgressiveLoading,
          trackListLoaded
        });
      } else {
        if (!has_initial_data_for_active_key_0 || is_full_refreshing) {
          trackListLoaded(1);
        }
      }
    }
  });
  const mutate = t6;
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  useEffect(() => () => {
    if (progressive_abort_ref.current) {
      progressive_abort_ref.current.abort();
    }
  }, t7);
  const should_check_new_models = current_models && !current_models.pagination.prev && poll_after_timestamp && !is_progressive_loading;
  const current_model_ids = new Set(current_models?.models.map(_temp) ?? []);
  const poll_base_filters = omitKey(base_filters, effective_poll_column);
  const {
    data: new_models
  } = useSWR(fetch && should_check_new_models && getCacheKey({
    ...poll_base_filters,
    limit: 1,
    [effective_poll_column]: {
      gt: poll_after_timestamp
    }
  }), () => list({
    ...poll_base_filters,
    limit: 1,
    [effective_poll_column]: {
      gt: poll_after_timestamp
    }
  }) as any, {
    ...(refresh_new_models ? revalidation_option : {
      ...revalidation_option,
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }),
    onSuccess: models => {
      if (!hasUnseenModels(models.models, current_model_ids)) {
        return;
      }
      if (current_models && current_models.count >= limit) {
        return;
      }
      mutate();
      if (!start_revalidation) {
        setTimeout(() => setStartRevalidation(true), revalidation_interval);
      }
    }
  });
  const model_ids = current_models ? current_models.models.map(_temp2) : [];
  const {
    data: revalidated_models
  } = useSWR(fetch && current_models && start_revalidation && model_ids.length > 0 && getCacheKey({
    ...base_filters,
    id: model_ids
  }), () => list({
    ...base_filters,
    id: model_ids
  }) as any, revalidation_option);
  let outdated_ids;
  let t8;
  let t9;
  if ($[1] !== current_models || $[2] !== getCacheKey || $[3] !== revalidated_models || $[4] !== start_revalidation) {
    outdated_ids = current_models && revalidated_models ? current_models.models.reduce((list_0, model_1) => {
      const revalidated_event = revalidated_models?.models.find(t10 => {
        const {
          id
        } = t10;
        return id === model_1.id;
      });
      if (!revalidated_event) {
        list_0.push(model_1.id);
      }
      return list_0;
    }, []) : [];
    t8 = useSWR;
    t9 = current_models && start_revalidation && outdated_ids.length > 0 && getCacheKey({
      id: outdated_ids,
      limit: outdated_ids.length
    });
    $[1] = current_models;
    $[2] = getCacheKey;
    $[3] = revalidated_models;
    $[4] = start_revalidation;
    $[5] = outdated_ids;
    $[6] = t8;
    $[7] = t9;
  } else {
    outdated_ids = $[5];
    t8 = $[6];
    t9 = $[7];
  }
  let t10;
  if ($[8] !== list || $[9] !== outdated_ids) {
    t10 = () => list({
      id: outdated_ids,
      limit: outdated_ids.length
    }) as any;
    $[8] = list;
    $[9] = outdated_ids;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = r => setLastOutadedResponse(r);
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  t8(t9, t10, {
    ...revalidation_option,
    onSuccess: t11
  });
  const {
    consolidated_models,
    cacheRefreshedEntry
  } = useConsolidatedModels(current_models, revalidated_models, last_outdated_response);
  let t12;
  if ($[12] !== consolidated_models || $[13] !== is_full_refreshing) {
    t12 = is_full_refreshing ? [] : consolidated_models;
    $[12] = consolidated_models;
    $[13] = is_full_refreshing;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const returned_models = t12;
  const t13 = is_full_refreshing ? false : !!current_models;
  const t14 = fetch && !current_models || is_full_refreshing || is_progressive_loading;
  const t15 = !!(current_models && current_models.count === limit && new_models && hasUnseenModels(new_models.models, current_model_ids));
  let t16;
  if ($[15] !== cacheRefreshedEntry || $[16] !== loaded_swr_key_ref || $[17] !== mutate || $[18] !== progressive_abort_ref || $[19] !== setIsProgressiveLoading || $[20] !== setLoadNewEvents || $[21] !== setPollAfterTimestamp || $[22] !== setRefreshNewEvents || $[23] !== setRefreshingKey || $[24] !== setStartRevalidation || $[25] !== swr_cache_key) {
    t16 = entry => {
      if (entry) {
        cacheRefreshedEntry(entry);
        mutate(data_1 => data_1 && {
          ...data_1,
          models: data_1.models.map(model_2 => model_2.id === entry.id ? entry : model_2)
        }, false);
      } else {
        if (progressive_abort_ref.current) {
          progressive_abort_ref.current.abort();
          progressive_abort_ref.current = null;
        }
        setIsProgressiveLoading(false);
        loaded_swr_key_ref.current = null;
        setPollAfterTimestamp(null);
        setStartRevalidation(false);
        setLoadNewEvents(false);
        setRefreshingKey(swr_cache_key);
        mutate();
      }
      setRefreshNewEvents(true);
    };
    $[15] = cacheRefreshedEntry;
    $[16] = loaded_swr_key_ref;
    $[17] = mutate;
    $[18] = progressive_abort_ref;
    $[19] = setIsProgressiveLoading;
    $[20] = setLoadNewEvents;
    $[21] = setPollAfterTimestamp;
    $[22] = setRefreshNewEvents;
    $[23] = setRefreshingKey;
    $[24] = setStartRevalidation;
    $[25] = swr_cache_key;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = is_progressive_loading ? null : current_models?.pagination.next;
  const t18 = current_models?.pagination.prev;
  const t19 = current_models?.pagination?.scanned_until ?? (is_progressive_loading && consolidated_models.length > 0 ? String((consolidated_models[consolidated_models.length - 1] as any)[order_by] || "") || null : null);
  let t20;
  if ($[27] !== is_progressive_loading || $[28] !== outdated_ids || $[29] !== returned_models || $[30] !== t13 || $[31] !== t14 || $[32] !== t15 || $[33] !== t16 || $[34] !== t17 || $[35] !== t18 || $[36] !== t19) {
    t20 = {
      models: returned_models,
      fetched: t13,
      count: returned_models.length,
      is_loading: t14,
      is_progressive_loading,
      outdated_ids,
      has_next_results: t15,
      refresh: t16,
      next: t17,
      prev: t18,
      scanned_until: t19
    };
    $[27] = is_progressive_loading;
    $[28] = outdated_ids;
    $[29] = returned_models;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  return t20;
};
export default useRefreshingList;
function _temp(model) {
  return String(model.id);
}
function _temp2(model_0) {
  return model_0.id;
}